import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { 
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { getData } from "../../Webservice/Webservice";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const Policies = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(location.state?.selectedTitle || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicies();
  }, []);

  useEffect(() => {
    if (location.state?.selectedTitle) {
      setSelected(location.state.selectedTitle);
    }
  }, [location.state]);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const res = await getData("policy/all");
      if (res && res.policies) {
        setPolicies(res.policies);
        if (res.policies.length > 0 && !selected) {
          setSelected(res.policies[0].title);
        }
      }
    } catch (error) {
      console.error("Error fetching policies:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedPolicy = policies.find((p) => p.title === selected);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#C8A054' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "#999" }} />}
          aria-label="breadcrumb"
          sx={{ mb: 6 }}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer", fontSize: "14px", fontWeight: 500, color: "#999" }}
          >
            HOME
          </Link>
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#222" }}>HELP CENTER</Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 8 }}>
          
          {/* Sidebar Navigation - Professional List */}
          <Box sx={{ width: { xs: "100%", md: "260px" }, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, letterSpacing: "1px", color: "#111", ...rubikfont }}>
                POLICIES
            </Typography>
            <List disablePadding>
              {policies.map((policy) => (
                <ListItemButton
                  key={policy._id}
                  onClick={() => setSelected(policy.title)}
                  sx={{
                    px: 0,
                    py: 1.5,
                    borderBottom: "1px solid #f0f0f0",
                    "&:hover": { bgcolor: "transparent", "& .MuiListItemText-primary": { color: "#C8A054" } },
                  }}
                >
                  <ListItemText
                    primary={policy.title}
                    primaryTypographyProps={{
                      fontWeight: selected === policy.title ? 600 : 400,
                      color: selected === policy.title ? "#C8A054" : "#666",
                      fontSize: "15px",
                      ...rubikfont,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedPolicy ? (
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 4,
                    color: "#111",
                    fontSize: { xs: "24px", md: "36px" },
                    ...rubikfont,
                    textTransform: "capitalize",
                  }}
                >
                  {selected}
                </Typography>

                <Box
                  sx={{
                    color: "#333",
                    lineHeight: 1.8,
                    fontSize: "16px",
                    "& h1, & h2, & h3": { 
                      mt: 4, mb: 2, color: "#111", fontWeight: 600, fontSize: "20px"
                    },
                    "& p": { mb: 3 },
                    "& ol, & ul": { pl: 3, mb: 3 },
                    "& li": { mb: 1 },
                    "& strong": { color: "#000", fontWeight: 600 },
                    ...rubikfont,
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedPolicy.contentHtml }}
                />
              </Box>
            ) : (
              <Box sx={{ py: 10, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>Select a policy from the sidebar</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Policies;
